import { FC, useState } from 'react';
import Page, { PageProps } from '@/components/page';
import { Stack } from '@mui/material';
import DataTable from '@/components/data-table';
import PageHeader from '@/components/page/header';
import { DtColumn, DtFilter, DtPage } from '@/components/data-table/helpers';
import OpenDrawerButton from '@/components/drawers/open-drawer-button';
import { QueryKey } from '@/lib/query-client';
import DomSize from '@/components/dom-size';

import Text from '@/components/text';
import Chip from '@/components/chip';
import NoneChip from '@/components/chip/none-chip';
import { startCase } from 'lodash';
import { getMcpServers } from '@/lib/services/mcp.service';
import { createUseStyles } from 'react-jss';
import Icon from '@/components/icon';
import PageBlurb from '@/components/page/blurb';
import useDrawer from '@/hooks/use-drawer.hook';

const columns: DtColumn[] = [
  { name: 'title', label: 'Server', flex: 2.5, sortable: true },
  { name: 'category', label: 'Category', flex: 0.6, sortable: true },
  { name: 'tags', label: 'Tags', flex: 2, sortable: false },
  { name: 'toolCount', label: 'Tools', flex: 0.4, sortable: false },
  { name: 'action', label: '', flex: 0.2, sortable: false },
];

const useStyles = createUseStyles({
  favicon: {
    width: 32,
    height: 32,
  },
});

const McpServersPage: FC<PageProps> = () => {
  const styles = useStyles();
  const { openDrawer } = useDrawer();
  const [selectedRowId, setSelectedRowId] = useState('');

  const handleView = (id: string) => {
    setSelectedRowId(id || '');
    openDrawer('mcp-server', {
      id,
      onClose: () => {
        setSelectedRowId('');
      },
    });
  };

  const loadData = async (pageParams: DtPage, filter?: DtFilter) => {
    const { page, pageSize, sort } = pageParams;
    const dataset = await getMcpServers(page, pageSize, sort, filter);

    return {
      ...dataset,
      rows: dataset.rows.map(({ id, title, description, tags, category, toolCount, icon }) => {
        const hasTags = !!tags.length;
        const hasImage = !!icon;
        const hasDescription = !!description && description !== title;

        const visbleTagCount = 2;
        const hiddenTagCount = tags.length - visbleTagCount;
        const TagEl = (
          <Stack direction="row" gap={0.5}>
            {tags.slice(0, visbleTagCount).map((tag) => (
              <Chip key={tag} label={tag} size="small" />
            ))}
            {hiddenTagCount > 0 && <Chip label={`+${hiddenTagCount} more`} size="small" />}
          </Stack>
        );

        return {
          id,
          title: (
            <Stack alignItems="center" direction="row" gap={2} width="100%">
              <Stack className={styles.favicon} alignItems="center" justifyContent="center">
                {hasImage ? (
                  <img src={icon} className={styles.favicon} />
                ) : (
                  <Icon name="question-mark" color="grey" />
                )}
              </Stack>
              <Stack maxWidth="85%">
                <Text size="small">{title}</Text>

                {hasDescription && (
                  <Text size="small" color="grey" dotdot>
                    {description}
                  </Text>
                )}
              </Stack>
            </Stack>
          ),
          category: category ? <Text size="small">{startCase(category)}</Text> : <NoneChip />,
          tags: hasTags ? TagEl : <NoneChip />,
          toolCount,
          action: <OpenDrawerButton onClick={() => handleView(id)} />,
        };
      }),
    };
  };

  return (
    <Page title="MCP Servers">
      <PageHeader>
        <PageBlurb>
          Browse MCP servers and tools to expand your AI functionality. Evaluate each integration's
          capabilities and access levels to ensure they match your intended use cases and risk
          tolerance.
        </PageBlurb>
      </PageHeader>
      <DomSize>
        <DataTable
          queryKey={QueryKey.PublicServicesDataset}
          searchPlaceholder="Search by name"
          columns={columns}
          onLoad={loadData}
          search
          rowHeight={86}
          selectedRowId={selectedRowId}
          sort={{ columnName: 'title', direction: 'asc' }}
        />
      </DomSize>
    </Page>
  );
};

export default McpServersPage;
